import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import Form from "react-bootstrap/Form";
import { PulseLoader } from "react-spinners";
import ProjectPageLeftPanel from "./common-components/project-page-left-panel";
import ProjectPageRightPanel from "./common-components/project-page-right-panel";
import { LoginContext } from "../../../../services/api/authentication-api";
import { Formik } from "formik";
import {
  validationSchema,
  fetchCreateData,
  useHandleFormChange,
  createProject,
  editProjectWithTaskDueDateExtension,
  setCancel,
} from "./helpers/project-helpers";
import "./project-form.scss";
import ProjectSubmitCancel from "./common-components/project-submit-cancel";
import { errorNotification } from "../../../../redux/notifications";
import CancelModal from "../../../../components/cancel-modal/cancel-modal";
import ConfirmationModal from "../../../../components/confirmation-modal/confirmation-modal";
import { useDispatch } from "react-redux";
import TransferModal from "../project-creation/project-ownership-transfer";
import EndDateUpdateModal from "../project-creation/project-end-date-update-modal";
import { GetProjectTasksCount } from "../../../../services/api/project-api";
import { priorityOrder } from "../../../tasks/scenes/telerik-tasks/static-task-list/task-util";
import { isDynamicFieldsValid } from "../../../../components/DynamicField/Tasks/utils/utils";

const ProjectForm = ({ isEdit, initialProject }) => {
  const [project, setProject] = useState(initialProject);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [alerts, setAlerts] = useState({
    showAlert: false,
    alertMessage: "",
  });
  const [panelVisibility, setPanelVisibility] = useState({
    showCancelPanel: false,
    showConfirmPanel: false,
  });
  const [selectedOwner, setSelectedOwner] = useState(initialProject?.Owner);
  const [selectedOwners, setSelectedOwners] = useState(initialProject?.Owners);
  const [projectTasksCount, setProjectTasksCount] = useState(0);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [projectTransferType, setProjectTransferType] = useState(0);
  const [isEndDateModalOpen, setIsEndDateModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false); 
  const [isMultiOwner, setIsMultiOwner] = useState(initialProject?.IsMultiOwner ?? true);
  const [canTransferOwner, setCanTransferOwner] = useState(initialProject?.CanTransferOwner !== false);

  const location = useLocation();
  const fromDashboard = location.state?.fromDashboard;

  const user = useContext(LoginContext);
  const labels = user?.currentUser?.objectLabels;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEdit) {
      fetchCreateData({
        user,
        setProjectTemplates,
        setSelectedOwner,
        setSelectedOwners,
        setProject,
        setLoaded,
        setAlerts,
        isMultiOwner: isMultiOwner,
        setIsMultiOwner,
      });
    } else {
      setLoaded(true);
      setProjectTemplates(initialProject.Templates);
      getProjectTasksCount(initialProject.Id);
    }
  }, [isEdit, initialProject?.Id, initialProject?.Templates, initialProject?.IsMultiOwner, user]);

  useEffect(() => {
    setCanTransferOwner(initialProject?.CanTransferOwner !== false);
  }, [initialProject?.CanTransferOwner]);

  const getProjectTasksCount = (projectID) => {
    GetProjectTasksCount(projectID).then((result) => {
      setProjectTasksCount(result.data ?? 0);
    });
  };

  useEffect(() => {
    if (alerts.showAlert) {
      dispatch(errorNotification({
        header: `Unable to Create ${labels?.ProjectSingular ?? "Project"}`,
        message: alerts.alertMessage,
      }));
    }
  }, [alerts]);

  const setShowEditedModal = () => {
    setPanelVisibility((prev) => ({ ...prev, showConfirmPanel: true }));
  };

  const setErrorMessage = (error) => {
    dispatch(errorNotification({
      header: `Unable to Edit ${labels?.ProjectSingular ?? "Project"}`,
      message: error?.toString(),
    }));
  };

  const handleFormChange = useHandleFormChange({
    projectTemplates,
    setProject,
    project,
    setSelectedOwner,
    setSelectedOwners,
    isMultiOwner,
    setIsMultiOwner,
    setCanTransferOwner,
    setIsTransferModalOpen,
    currentUserId: user.userID,
  });

  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);
    let updatedOwners = (values.Owners || []).map(owner => {
      const ownerObj = typeof owner === "object" && owner ? owner : {Id: owner, IsGroup: false};
      if (ownerObj.IsGroup) {
          const matchingGroup = values.allUsers.find(user => user.GroupId === ownerObj.Id);
          if (matchingGroup) {
              return { ...ownerObj, Group: matchingGroup };
          }
      }
      return ownerObj;
  });
    let obj = {
      ...values,
      Owners: updatedOwners.length > 0 ? updatedOwners : [{Id: values.OwnerId, IsGroup: false}],
      dynamicFields: dynamicFields?.map((field) => ({
        DynamicFieldId: field.Id,
        Data: field.Data,
        ObjectId: field.ObjectId,
        Files: field.Files,
      })),
    };
    try {
      if (isEdit) {
        await editProjectWithTaskDueDateExtension({ project: obj, setPanelVisibility, setAlerts });
      } else {
        await createProject({ project: obj, setPanelVisibility, setAlerts });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const validateProjectName = (name) => {
    if(name.trim() === ""){
      return false;
    }

    return isDynamicFieldsValid(dynamicFields);
  };

  return (
    <div className="project-form-container dashboard-container-shadow">
      <Helmet>
        <title>TRIYO · {isEdit ? "Edit" : "Create"} {labels?.ProjectSingular ?? "Project"}</title>
      </Helmet>
      {panelVisibility.showCancelPanel &&
        <CancelModal
          show={panelVisibility.showCancelPanel}
          onHide={() => {
            setPanelVisibility((prev) => ({ ...prev, showCancelPanel: false }));
          }}
          redirectURL={"/projects"}
        />       
      }
      {panelVisibility.showConfirmPanel && (
        <ConfirmationModal
          show={panelVisibility.showConfirmPanel}
          onHide={() => {}}
          redirectURL={fromDashboard ? "/dashboard" : "/projects"}
          header={`${isEdit ? "Edit" : "Create"} ${labels?.ProjectSingular ?? "Project"} Confirmation`}
          confirmationText={`${labels?.ProjectSingular ?? "Project"} has been ${
            isEdit ? "edited" : "created"
          } successfully.`}
        />
      )}
      {!loaded ? (
        <div className="loading-overlay ">
          <div className="loading-spinner-wrapper">
            <PulseLoader className="loading-spinner"></PulseLoader>
          </div>
        </div>
      ) : (
        <>
          <h4 className="panel-title">{isEdit ? "Edit" : "Create"} {labels?.ProjectSingular ?? "Project"}</h4>
          <Formik
            key={`tpl:${project.ParentTemplateId ?? 'na'}`}
            enableReinitialize
            initialValues={project}
            validationSchema={validationSchema(labels)}
            onSubmit={handleSubmit}
          >
            {({ values, errors, setFieldValue }) => (
              <Form>
                <div className="form-panels">
                  <div className="left-panel-container">
                    <ProjectPageLeftPanel
                      key={`${values.ParentTemplateId ?? 'na'}`}
                      priorities={priorityOrder}
                      data={values}
                      selectedOwner={selectedOwner}
                      selectedOwners={selectedOwners}
                      isMultiOwner={isMultiOwner}
                      canTransferOwner={canTransferOwner}
                      projectTemplates={projectTemplates.map(
                        (template) => template.text
                      )}
                      setFormState={(field, value) =>
                        handleFormChange(field, value, setFieldValue)
                      }
                      errors={errors}
                      user={user}
                      isEdit={isEdit}
                      labels={labels}
                    />
                  </div>
                  <div className="right-panel-container">
                    <ProjectPageRightPanel
                      data={values}
                      dynamicFields={dynamicFields}
                      setDynamicFields={setDynamicFields}
                      setFormState={(field, value) =>
                        handleFormChange(field, value, setFieldValue)
                      }
                    />
                  </div>
                </div>
                {isEdit && !isMultiOwner && (
                  <React.Fragment>
                    <TransferModal
                      projectId={project.Id}
                      dateFormatID={user?.currentUser?.department?.DateFormatID}
                      originalOwner={initialProject?.Owner}
                      selectedOwner={selectedOwner}
                      setSelectedOwner={setSelectedOwner}
                      originalProjectEndDate={initialProject.End}
                      selectedProjectEndDate={project.End}
                      projectTasksCount={projectTasksCount ?? 0}
                      isTransferModalOpen={isTransferModalOpen}
                      setIsTransferModalOpen={setIsTransferModalOpen}
                      projectTransferType={projectTransferType}
                      setProjectTransferType={setProjectTransferType}
                      setShowEditedModal={setShowEditedModal}
                      setErrorModal={setErrorMessage}
                      labels={labels}
                    />
                    {projectTasksCount > 0 && (
                      <EndDateUpdateModal
                        projectId={project.Id}
                        dateFormatID={user?.currentUser?.department?.DateFormatID}
                        originalProjectEndDate={
                          initialProject.End
                        }
                        selectedProjectEndDate={
                          project.End
                        }
                        isEndDateModalOpen={isEndDateModalOpen}
                        setIsEndDateModalOpen={setIsEndDateModalOpen}
                        setShowEditedModal={setShowEditedModal}
                        setErrorModal={setErrorMessage}
                      />
                    )} 
                  </React.Fragment>
                )}
                <ProjectSubmitCancel
                  isSubmitting={submitting}
                  isValid={validateProjectName(values.Name)}
                  isEdit={isEdit}
                  submitForm={() =>
                    handleSubmit(values)
                  }
                  setCancel={() => {
                    setCancel({ setPanelVisibility });
                  }}
                  labels={labels}
                />
              </Form>
            )}
          </Formik>
        </>
      )}
    </div>
  );
};

ProjectForm.propTypes = {
  isEdit: PropTypes.bool.isRequired,
  initialProject: PropTypes.object.isRequired,
};

export default ProjectForm;